import { z } from 'zod';

export const WEEK_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type WeekDay = (typeof WEEK_DAYS)[number];

export const MAX_SEGMENTS_PER_DAY = 4;

const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;

const segment = z.object({
  from: z.string().regex(CLOCK, { error: 'chatSettings:validation.clock' }),
  to: z.string().regex(CLOCK, { error: 'chatSettings:validation.clock' }),
});

export type HoursSegmentForm = z.infer<typeof segment>;

/** Segments of one day: `to` after `from`, no two overlapping, at most four. */
const daySegments = z
  .array(segment)
  .max(MAX_SEGMENTS_PER_DAY)
  .superRefine((segments, ctx) => {
    segments.forEach((current, index) => {
      if (!CLOCK.test(current.from) || !CLOCK.test(current.to)) return;
      if (current.to <= current.from) {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'to'],
          message: 'chatSettings:validation.segmentOrder',
        });
        return;
      }
      const overlaps = segments.some(
        (other, j) =>
          j < index &&
          CLOCK.test(other.from) &&
          CLOCK.test(other.to) &&
          current.from < other.to &&
          other.from < current.to,
      );
      if (overlaps) {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'from'],
          message: 'chatSettings:validation.segmentOverlap',
        });
      }
    });
  });

export const hoursSchema = z.object({
  hours: z.object({
    enabled: z.boolean(),
    /** Empty follows the store's timezone. */
    timezone: z.string(),
    days: z.object({
      mon: daySegments,
      tue: daySegments,
      wed: daySegments,
      thu: daySegments,
      fri: daySegments,
      sat: daySegments,
      sun: daySegments,
    }),
    offlineWhenNoAgentOnline: z.boolean(),
  }),
  behaviour: z.object({
    autoAssign: z.enum(['off', 'round_robin']),
  }),
});

export type HoursForm = z.infer<typeof hoursSchema>;
